import { Badge, Box, Button, Group, SegmentedControl, Text } from '@mantine/core';
import { Clock, Eye, X } from 'react-feather';

/**
 * MED-873 / 2.3c — persistent header bar shown above the Active Session Preview
 * page. Three logical zones:
 *
 *   ┌─────────────────────────────────────────────────────────────────────────┐
 *   │ [ic] Session Preview · Session N: Title · Step N: Section │             │
 *   │   ┌───────────────────────────────────────┐                             │
 *   │   │ [Leader View] | [Participant View]    │  [Preview only] [× Close]   │
 *   │   └───────────────────────────────────────┘                             │
 *   └─────────────────────────────────────────────────────────────────────────┘
 *
 * The "Leader View / Participant View" toggle is functional: it switches the
 * preview body between the leader (coach/peer-leader) panels and the read-only
 * participant slide view (the participant active-session experience, MED-805).
 */

export type PreviewViewMode = 'coach' | 'participant';

export interface ActiveSessionPreviewHeaderBarProps {
  sessionTitle: string;
  sessionNumber: number | null;
  sectionIndex: number | null;
  sectionTitle: string | null;
  viewMode: PreviewViewMode;
  onViewModeChange: (mode: PreviewViewMode) => void;
  onClose: () => void;
}

export function ActiveSessionPreviewHeaderBar({
  sessionTitle,
  sessionNumber,
  sectionIndex,
  sectionTitle,
  viewMode,
  onViewModeChange,
  onClose,
}: ActiveSessionPreviewHeaderBarProps) {
  const breadcrumbLeft =
    sessionNumber !== null ? `Session ${sessionNumber}: ${sessionTitle}` : sessionTitle;
  const breadcrumbRight =
    sectionIndex !== null && sectionTitle ? `Step ${sectionIndex + 1}: ${sectionTitle}` : null;

  return (
    <Box
      data-testid="active-session-preview-header-bar"
      style={{
        background: '#1A1A1A',
        color: '#FFFFFF',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        position: 'sticky',
        top: 0,
        zIndex: 200,
      }}
    >
      <Group gap={10} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
        <Group
          gap={6}
          align="center"
          wrap="nowrap"
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
            padding: '4px 10px',
            borderRadius: 999,
            flexShrink: 0,
          }}
        >
          <Eye size={14} aria-hidden="true" />
          <Text size="xs" fw={600} c="white" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
            Session Preview
          </Text>
        </Group>
        <Text size="sm" c="gray.4" truncate>
          {breadcrumbLeft}
          {breadcrumbRight && (
            <>
              {' · '}
              <Text component="span" c="white" fw={500}>
                {breadcrumbRight}
              </Text>
            </>
          )}
        </Text>
      </Group>

      {/*
        Functional view toggle: "Leader View" shows the leader panels,
        "Participant View" renders the read-only participant slide experience
        (MED-805). The internal value stays 'coach' for back-compat; only the
        user-facing label changed to "Leader View" per MED-1218 AC6.
      */}
      <SegmentedControl
        value={viewMode}
        onChange={(value) => onViewModeChange(value as PreviewViewMode)}
        data={[
          { value: 'coach', label: 'Leader View' },
          { value: 'participant', label: 'Participant View' },
        ]}
        size="xs"
        radius="xl"
        color="medleyGreen"
        data-testid="active-session-preview-view-toggle"
      />

      <Group gap={10} align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
        <Badge
          color="warningAmber"
          variant="filled"
          radius="xl"
          size="sm"
          leftSection={<Clock size={10} aria-hidden="true" />}
          style={{ textTransform: 'none', fontWeight: 500 }}
          data-testid="active-session-preview-disclaimer"
        >
          Preview only — read only
        </Badge>
        <Button
          variant="subtle"
          size="xs"
          color="gray"
          leftSection={<X size={14} />}
          onClick={onClose}
          c="white"
          data-testid="active-session-preview-close-button"
          styles={{
            root: { color: '#FFFFFF' },
          }}
        >
          Close Preview
        </Button>
      </Group>
    </Box>
  );
}
